import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Lightbulb, Wrench, Search, Heart, Target, Megaphone, ArrowLeft, TrendingUp, Users, CheckCircle, AlertCircle, Download, ChevronDown, ChevronUp, FileText, Sparkles, Briefcase, MessageSquare, Shield, UserPlus, GraduationCap, Rocket, Crown, Battery } from 'lucide-react';
import ShareResultsCard from '../components/ShareResultsCard';

const ResultsPage = ({ auth }) => {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [primaryProfile, setPrimaryProfile] = useState(null);
  const [secondaryProfile, setSecondaryProfile] = useState(null);
  const [pairingReport, setPairingReport] = useState(null);
  const [marketplaceProfile, setMarketplaceProfile] = useState(null);
  const [roleSuggestions, setRoleSuggestions] = useState([]);
  const [marketplaceRoles, setMarketplaceRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scoresExpanded, setScoresExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/assessment/result/${auth.user.id}`);
      setResult(response.data);

      // Fetch dimension profiles
      const primaryRes = await axios.get(`${API}/dimension/${response.data.primary_dimension}`);
      setPrimaryProfile(primaryRes.data);

      const secondaryRes = await axios.get(`${API}/dimension/${response.data.secondary_dimension}`);
      setSecondaryProfile(secondaryRes.data);

      // Fetch pairing report
      const pairingCode = [response.data.primary_dimension, response.data.secondary_dimension].sort().join('');
      try {
        const pairingRes = await axios.get(`${API}/assessment/pairing/${pairingCode}`);
        setPairingReport(pairingRes.data);
      } catch (err) {
        console.error('Could not fetch pairing report:', err);
      }

      // Fetch marketplace profile
      try {
        const marketplaceRes = await axios.get(`${API}/assessment/marketplace-profile/${pairingCode}`);
        setMarketplaceProfile(marketplaceRes.data);
      } catch (err) {
        console.error('Could not fetch marketplace profile:', err);
      }

      // Fetch role suggestions
      const rolesRes = await axios.get(`${API}/roles/suggestions/${response.data.primary_dimension}`);
      setRoleSuggestions(rolesRes.data.ministry_roles || rolesRes.data.roles || []);
      setMarketplaceRoles(rolesRes.data.marketplace_roles || []);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No assessment found. Please take the assessment first.');
        navigate('/assessment');
      } else {
        toast.error('Failed to load results');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadMinistryPDF = async () => {
    try {
      const response = await axios.get(`${API}/assessment/download-ministry-pdf/${auth.user.id}`, {
        responseType: 'blob'
      });
      
      // Check if response is actually a PDF (not an error response)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.detail || 'Failed to download');
      }
      
      const pairingCode = [result.primary_dimension, result.secondary_dimension].sort().join('');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BUILDR_Ministry_Report_${pairingCode}_${auth.user.name.replace(' ', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Ministry report downloaded!');
    } catch (error) {
      console.error('Ministry PDF download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download Ministry PDF. Please try again.');
    }
  };

  const handleDownloadMarketplacePDF = async () => {
    try {
      const response = await axios.get(`${API}/assessment/download-marketplace-pdf/${auth.user.id}`, {
        responseType: 'blob'
      });
      
      // Check if response is actually a PDF (not an error response)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.detail || 'Failed to download');
      }
      
      const pairingCode = [result.primary_dimension, result.secondary_dimension].sort().join('');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BUILDR_Marketplace_Report_${pairingCode}_${auth.user.name.replace(' ', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Marketplace report downloaded!');
    } catch (error) {
      console.error('Marketplace PDF download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download Marketplace PDF. Please try again.');
    }
  };

  const handleDownloadCompletePDF = async () => {
    try {
      const response = await axios.get(`${API}/assessment/download-complete-pdf/${auth.user.id}`, {
        responseType: 'blob'
      });
      
      // Check if response is actually a PDF (not an error response)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.detail || 'Failed to download');
      }
      
      const pairingCode = [result.primary_dimension, result.secondary_dimension].sort().join('');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BUILDR_Complete_Report_${pairingCode}_${auth.user.name.replace(' ', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Complete report downloaded!');
    } catch (error) {
      console.error('Complete PDF download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download Complete PDF. Please try again.');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/assessment/download-pdf/${auth.user.id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BUILDR_Assessment_${auth.user.name.replace(' ', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleDownloadPairingReport = async () => {
    try {
      const response = await axios.get(`${API}/assessment/download-pairing-pdf/${auth.user.id}`, {
        responseType: 'blob'
      });
      
      const pairingCode = [result.primary_dimension, result.secondary_dimension].sort().join('');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BUILDR_Pairing_Report_${pairingCode}_${auth.user.name.replace(' ', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Pairing report downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download pairing report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading your results...</div>
      </div>
    );
  }

  const dimensionIcons = {
    B: Lightbulb,
    U: Wrench,
    I: Search,
    L: Heart,
    D: Target,
    R: Megaphone
  };

  const dimensionColors = {
    B: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
    U: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
    I: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
    L: { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300' },
    D: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
    R: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' }
  };

  const dimensionNames = {
    B: 'Brainstorm',
    U: 'Uncover',
    I: 'Inspect',
    L: 'Lift',
    D: 'Deliver',
    R: 'Rally'
  };

  const PrimaryIcon = dimensionIcons[primaryProfile.code];
  const SecondaryIcon = dimensionIcons[secondaryProfile.code];
  const primaryColor = dimensionColors[primaryProfile.code];
  const secondaryColor = dimensionColors[secondaryProfile.code];
  const pairingCode = [result.primary_dimension, result.secondary_dimension].sort().join('');

  const maxScore = Math.max(...Object.values(result.scores));

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          <h1 className="text-5xl font-bold mb-4 text-gray-800">Your BUILDR Profile</h1>
          <p className="text-xl text-gray-600">Discover your unique strengths and ideal roles</p>
        </div>

        {/* PAIRING CODE HERO - The new prominent display */}
        <Card className="mb-8 border-4 border-indigo-400 shadow-2xl bg-gradient-to-br from-indigo-50 to-purple-50" data-testid="pairing-code-card">
          <CardContent className="pt-8 pb-8">
            <div className="text-center mb-6">
              <Badge className="bg-indigo-600 text-white text-sm px-4 py-1 mb-4">Your BUILDR Pairing</Badge>
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className={`w-16 h-16 ${primaryColor.bg} rounded-full flex items-center justify-center`}>
                  <PrimaryIcon className={`w-8 h-8 ${primaryColor.text}`} />
                </div>
                <h2 className="text-7xl font-bold text-indigo-700" data-testid="pairing-code-display">{pairingCode}</h2>
                <div className={`w-16 h-16 ${secondaryColor.bg} rounded-full flex items-center justify-center`}>
                  <SecondaryIcon className={`w-8 h-8 ${secondaryColor.text}`} />
                </div>
              </div>
              {pairingReport && (
                <>
                  <h3 className="text-2xl font-semibold text-gray-800 mb-2">{pairingReport.name}</h3>
                  <p className="text-lg text-indigo-600 italic mb-4">"{pairingReport.tagline}"</p>
                </>
              )}
              <div className="flex items-center justify-center gap-2 text-gray-600">
                <span className={`font-semibold ${primaryColor.text}`}>{dimensionNames[result.primary_dimension]}</span>
                <span>+</span>
                <span className={`font-semibold ${secondaryColor.text}`}>{dimensionNames[result.secondary_dimension]}</span>
              </div>
            </div>

            {pairingReport && (
              <div className="max-w-3xl mx-auto">
                <p className="text-gray-700 text-center leading-relaxed">{pairingReport.description}</p>
              </div>
            )}

            {/* Download Pairing Report Button */}
            <div className="flex justify-center mt-6">
              <Button
                data-testid="download-pairing-report-btn"
                onClick={handleDownloadPairingReport}
                size="lg"
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <FileText className="mr-2 h-5 w-5" />
                Download Your {pairingCode} Report
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* What Encourages / Discourages You */}
        {pairingReport && (
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700">
                  <Sparkles className="h-6 w-6" />
                  What Encourages You
                </CardTitle>
                <CardDescription>Environments where you thrive</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {pairingReport.what_encourages?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-700">
                  <AlertCircle className="h-6 w-6" />
                  What Discourages You
                </CardTitle>
                <CardDescription>Environments that may drain you</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {pairingReport.what_discourages?.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Ideal Ministry Context */}
        {pairingReport?.ideal_ministry_context && (
          <Card className="mb-8 border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-6 w-6 text-indigo-600" />
                Your Ideal Ministry Context
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg text-gray-700 leading-relaxed">{pairingReport.ideal_ministry_context}</p>
            </CardContent>
          </Card>
        )}

        {/* Collapsible Scores Overview */}
        <Collapsible open={scoresExpanded} onOpenChange={setScoresExpanded} className="mb-8">
          <Card className="fade-in">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl flex items-center gap-2">
                      Complete BUILDR Score Breakdown
                      {scoresExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </CardTitle>
                    <CardDescription>Click to {scoresExpanded ? 'collapse' : 'expand'} your detailed scores</CardDescription>
                  </div>
                  <Badge variant="outline" className="text-sm">
                    {scoresExpanded ? 'Expanded' : 'Collapsed'}
                  </Badge>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="space-y-6">
                  {Object.entries(result.scores)
                    .sort(([,a], [,b]) => b - a)
                    .map(([dimension, score]) => {
                      const Icon = dimensionIcons[dimension];
                      const color = dimensionColors[dimension];
                      const percentage = (score / maxScore) * 100;
                      const isPrimary = dimension === result.primary_dimension;
                      const isSecondary = dimension === result.secondary_dimension;
                      return (
                        <div key={dimension} data-testid={`score-${dimension}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 ${color.bg} rounded-full flex items-center justify-center`}>
                                <Icon className={`w-5 h-5 ${color.text}`} />
                              </div>
                              <span className="font-semibold text-lg">
                                {dimensionNames[dimension]}
                                {isPrimary && <Badge className="ml-2 bg-indigo-600 text-xs">Primary</Badge>}
                                {isSecondary && <Badge className="ml-2 bg-purple-500 text-xs">Secondary</Badge>}
                              </span>
                            </div>
                            <span className="text-lg font-bold">{score}/35</span>
                          </div>
                          <Progress value={percentage} className="h-3" />
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Detailed Profiles */}
        <Tabs defaultValue="primary" className="mb-8">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="primary" data-testid="primary-tab">Primary: {primaryProfile.name}</TabsTrigger>
            <TabsTrigger value="secondary" data-testid="secondary-tab">Secondary: {secondaryProfile.name}</TabsTrigger>
          </TabsList>

          <TabsContent value="primary" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-6 w-6" />
                  How You Think & Speak
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold text-lg mb-2 text-gray-700">How You Think:</h4>
                  <p className="text-gray-600 leading-relaxed">{primaryProfile.how_they_think}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-lg mb-2 text-gray-700">How You Speak:</h4>
                  <p className="text-gray-600 leading-relaxed">{primaryProfile.how_they_speak}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-6 w-6" />
                  What Others Say About You
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed italic">{primaryProfile.what_others_say}</p>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    Your Strengths
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {primaryProfile.strengths.map((strength, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-green-600 mt-1">✓</span>
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-6 w-6 text-orange-600" />
                    Growth Opportunities
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {primaryProfile.growth_areas.map((area, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-orange-600 mt-1">→</span>
                        <span>{area}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Best Ministry Roles for You</CardTitle>
                <CardDescription>Positions where you'll naturally excel based on your {primaryProfile.name} strength</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-3">
                  {primaryProfile.best_roles.map((role, idx) => (
                    <div key={idx} className={`p-4 ${primaryColor.bg} rounded-lg border ${primaryColor.border}`}>
                      <span className="font-semibold">{role}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {primaryProfile.great_in && (
              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-6 w-6 text-indigo-600" />
                    You're Great In
                  </CardTitle>
                  <CardDescription>Environments where {primaryProfile.name}s thrive</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {primaryProfile.great_in.map((context, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-indigo-600 mt-1 text-xl">★</span>
                        <span className="text-lg font-medium">{context}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Support Roles That Help You Thrive</CardTitle>
                <CardDescription>Team members that complement your strengths</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {primaryProfile.support_roles.map((support, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                      <span className="text-indigo-600 font-bold mt-1">•</span>
                      <span>{support}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Blind Spots */}
            {primaryProfile.blind_spots && (
              <Card className="bg-gradient-to-br from-orange-50 to-yellow-50 border-2 border-orange-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-6 w-6 text-orange-600" />
                    Potential Blind Spots
                  </CardTitle>
                  <CardDescription>Areas to watch out for as a {primaryProfile.name}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {primaryProfile.blind_spots.map((blind, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-orange-600 mt-1">⚠</span>
                        <span className="text-gray-700">{blind}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Stress Indicators */}
            {primaryProfile.stress_indicators && (
              <Card className="bg-gradient-to-br from-red-50 to-pink-50 border-2 border-red-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                    Stress Indicators
                  </CardTitle>
                  <CardDescription>Situations that may drain or stress you</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {primaryProfile.stress_indicators.map((stress, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-red-600 mt-1">⚡</span>
                        <span className="text-gray-700">{stress}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Communication Style */}
            {primaryProfile.communication_style && (
              <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-6 w-6 text-blue-600" />
                    Your Communication Style
                  </CardTitle>
                  <CardDescription>How you naturally communicate and connect</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-gray-700 leading-relaxed">{primaryProfile.communication_style.overview}</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-800 mb-2">Communication Strengths</h4>
                      <ul className="space-y-1">
                        {primaryProfile.communication_style.strengths.map((strength, idx) => (
                          <li key={idx} className="text-sm text-green-700">✓ {strength}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <h4 className="font-semibold text-orange-800 mb-2">Communication Challenges</h4>
                      <ul className="space-y-1">
                        {primaryProfile.communication_style.challenges.map((challenge, idx) => (
                          <li key={idx} className="text-sm text-orange-700">→ {challenge}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Development Path */}
            {primaryProfile.development_path && (
              <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-6 w-6 text-purple-600" />
                    Your Development Path
                  </CardTitle>
                  <CardDescription>Suggested growth journey for {primaryProfile.name}s</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {primaryProfile.development_path.map((phase, idx) => (
                      <div key={idx} className="relative pl-8 pb-4 border-l-2 border-purple-300 last:border-l-0">
                        <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-purple-500"></div>
                        <div className="mb-1">
                          <Badge className="bg-purple-100 text-purple-800">{phase.phase}</Badge>
                        </div>
                        <p className="text-gray-700 leading-relaxed">{phase.focus}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="secondary" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">{secondaryProfile.name} - Your Secondary Strength</CardTitle>
                <CardDescription className="text-base">{secondaryProfile.tagline}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-6">{secondaryProfile.description}</p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-lg mb-2">How You Think:</h4>
                    <p className="text-gray-600">{secondaryProfile.how_they_think}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Best Roles:</h4>
                    <div className="flex flex-wrap gap-2">
                      {secondaryProfile.best_roles.slice(0, 4).map((role, idx) => (
                        <Badge key={idx} variant="outline" className="text-sm">{role}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Role Suggestions */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Heart className="h-6 w-6 text-indigo-600" />
              Recommended Ministry Roles
            </CardTitle>
            <CardDescription>Based on your {primaryProfile.name} strength - where you'll thrive in church settings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {roleSuggestions.map((role, idx) => (
                <div key={idx} className="p-6 border-2 rounded-lg hover:border-indigo-300 transition-colors bg-gradient-to-r from-indigo-50 to-purple-50" data-testid={`role-suggestion-${idx}`}>
                  <h3 className="text-xl font-semibold mb-2">{role.title}</h3>
                  <p className="text-gray-600">{role.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Marketplace Role Suggestions */}
        {marketplaceRoles.length > 0 && (
          <Card className="mb-8 border-2 border-emerald-200">
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50">
              <CardTitle className="text-2xl flex items-center gap-2">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
                Marketplace Roles Where You'll Thrive
              </CardTitle>
              <CardDescription>Based on your {primaryProfile.name} strength - where you'll excel in business and organizations</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-2 gap-4">
                {marketplaceRoles.map((role, idx) => (
                  <div key={idx} className="p-5 border-2 rounded-lg hover:border-emerald-400 transition-colors bg-white" data-testid={`marketplace-role-${idx}`}>
                    <h3 className="text-lg font-semibold mb-2 text-emerald-800">{role.title}</h3>
                    <p className="text-gray-600 text-sm">{role.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== MARKETPLACE PROFILE SECTIONS ===== */}
        {marketplaceProfile && (
          <>
            {/* Section 1: How You Add Value */}
            <Card className="mb-8 border-2 border-blue-200">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Briefcase className="h-6 w-6 text-blue-600" />
                  How You Add Value on a Team
                </CardTitle>
                <CardDescription>Your core contribution and where you belong</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <p className="text-gray-700 text-lg leading-relaxed">{marketplaceProfile.core_contribution?.summary}</p>
                
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">Problems You Solve</h4>
                    <ul className="space-y-1">
                      {marketplaceProfile.core_contribution?.problems_you_solve?.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2">Meetings You Should Attend</h4>
                    <ul className="space-y-1">
                      {marketplaceProfile.core_contribution?.meetings_you_should_attend?.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-2">💡 Value Without Burnout</h4>
                  <p className="text-gray-700">{marketplaceProfile.core_contribution?.value_without_burnout}</p>
                </div>
              </CardContent>
            </Card>

            {/* Section 2: Work Environment Fit */}
            <Card className="mb-8 border-2 border-purple-200">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Target className="h-6 w-6 text-purple-600" />
                  Work Environment Fit
                </CardTitle>
                <CardDescription>Where you thrive vs. where you struggle</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      You Thrive In
                    </h4>
                    <ul className="space-y-2">
                      {marketplaceProfile.work_environment?.thrives_in?.map((item, idx) => (
                        <li key={idx} className="text-gray-700 flex items-start gap-2">
                          <span className="text-green-600">✓</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      You Struggle In
                    </h4>
                    <ul className="space-y-2">
                      {marketplaceProfile.work_environment?.struggles_in?.map((item, idx) => (
                        <li key={idx} className="text-gray-700 flex items-start gap-2">
                          <span className="text-red-600">✗</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4 mt-4">
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <div className="text-sm text-gray-500">Ideal Pace</div>
                    <div className="font-medium">{marketplaceProfile.work_environment?.ideal_pace}</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <div className="text-sm text-gray-500">Structure</div>
                    <div className="font-medium">{marketplaceProfile.work_environment?.structure_preference}</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <div className="text-sm text-gray-500">Autonomy</div>
                    <div className="font-medium">{marketplaceProfile.work_environment?.autonomy_level}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 3: Communication Style */}
            <Card className="mb-8 border-2 border-cyan-200">
              <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <MessageSquare className="h-6 w-6 text-cyan-600" />
                  How You Communicate Best
                </CardTitle>
                <CardDescription>Your communication style and preferences</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <p className="text-gray-700 text-lg">{marketplaceProfile.communication_style?.how_you_speak}</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <h4 className="font-semibold text-orange-800 mb-2">What Frustrates You</h4>
                    <ul className="space-y-1">
                      {marketplaceProfile.communication_style?.what_frustrates_you?.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-700">• {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2">How Others Should Communicate With You</h4>
                    <ul className="space-y-1">
                      {marketplaceProfile.communication_style?.how_others_should_communicate?.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-700">• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 4: Common Misuses of Strengths */}
            <Card className="mb-8 border-2 border-orange-200">
              <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Shield className="h-6 w-6 text-orange-600" />
                  Common Misuses of Your Strengths
                </CardTitle>
                <CardDescription>Red flags and guardrails you need</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <h4 className="font-semibold text-orange-800 mb-2">⚠️ When Your Strengths Work Against You</h4>
                  <ul className="space-y-2">
                    {marketplaceProfile.misused_strengths?.when_overused?.map((item, idx) => (
                      <li key={idx} className="text-gray-700">• {item}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h4 className="font-semibold text-yellow-800 mb-2">How Others May Misunderstand You</h4>
                  <p className="text-gray-700">{marketplaceProfile.misused_strengths?.how_others_misunderstand_you}</p>
                </div>
                
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-2">Under Stress</h4>
                  <p className="text-gray-700">{marketplaceProfile.misused_strengths?.stress_behavior}</p>
                </div>
                
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">🛡️ Guardrails You Need</h4>
                  <ul className="space-y-1">
                    {marketplaceProfile.misused_strengths?.guardrails_needed?.map((item, idx) => (
                      <li key={idx} className="text-gray-700">→ {item}</li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Section 5: Best Team Pairings */}
            <Card className="mb-8 border-2 border-indigo-200">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-violet-50">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <UserPlus className="h-6 w-6 text-indigo-600" />
                  Best Pairings & Team Chemistry
                </CardTitle>
                <CardDescription>Who complements you best</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-indigo-50 rounded-lg">
                    <h4 className="font-semibold text-indigo-800 mb-3">🤝 Best Complements</h4>
                    {marketplaceProfile.team_pairings?.best_complements?.map((p, idx) => (
                      <div key={idx} className="mb-2 p-2 bg-white rounded border">
                        <Badge className="bg-indigo-600 mb-1">{p.pairing}</Badge>
                        <p className="text-sm text-gray-700">{p.reason}</p>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-semibold text-purple-800 mb-3">⚡ Energizing Partnerships</h4>
                    {marketplaceProfile.team_pairings?.energizing_partnerships?.map((p, idx) => (
                      <div key={idx} className="mb-2 p-2 bg-white rounded border">
                        <Badge className="bg-purple-600 mb-1">{p.pairing}</Badge>
                        <p className="text-sm text-gray-700">{p.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2">💡 Collaboration Tip</h4>
                  <p className="text-gray-700">{marketplaceProfile.team_pairings?.collaboration_tip}</p>
                </div>
              </CardContent>
            </Card>

            {/* Section 6: Career Pathways */}
            <Card className="mb-8 border-2 border-teal-200">
              <CardHeader className="bg-gradient-to-r from-teal-50 to-emerald-50">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <GraduationCap className="h-6 w-6 text-teal-600" />
                  Career Pathways & Growth
                </CardTitle>
                <CardDescription>Your natural progression and development areas</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="p-4 bg-teal-50 rounded-lg">
                  <h4 className="font-semibold text-teal-800 mb-2">📈 Natural Career Progression</h4>
                  {marketplaceProfile.career_pathways?.natural_progression?.map((path, idx) => (
                    <p key={idx} className="text-gray-700 mb-1">• {path}</p>
                  ))}
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2">🎯 Skills to Develop</h4>
                    <ul className="space-y-1">
                      {marketplaceProfile.career_pathways?.skills_to_develop?.map((skill, idx) => (
                        <li key={idx} className="text-sm text-gray-700">→ {skill}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">↔️ Lateral Move Options</h4>
                    <ul className="space-y-1">
                      {marketplaceProfile.career_pathways?.lateral_moves?.map((move, idx) => (
                        <li key={idx} className="text-sm text-gray-700">• {move}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-2">⚠️ Watch Out For</h4>
                  <p className="text-gray-700">{marketplaceProfile.career_pathways?.watch_out_for}</p>
                </div>
              </CardContent>
            </Card>

            {/* Section 7: Resume & Interview Language */}
            <Card className="mb-8 border-2 border-slate-200">
              <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <FileText className="h-6 w-6 text-slate-600" />
                  Resume & Interview Language
                </CardTitle>
                <CardDescription>How to describe yourself professionally</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="p-4 bg-slate-100 rounded-lg border-l-4 border-slate-600">
                  <h4 className="font-semibold text-slate-800 mb-2">Professional Headline</h4>
                  <p className="text-lg text-gray-800 italic">"{marketplaceProfile.resume_language?.headline}"</p>
                </div>
                
                <div className="p-4 bg-white rounded-lg border">
                  <h4 className="font-semibold text-gray-800 mb-3">Resume Bullet Points (Copy & Use)</h4>
                  <ul className="space-y-2">
                    {marketplaceProfile.resume_language?.bullet_points?.map((bullet, idx) => (
                      <li key={idx} className="text-gray-700 p-2 bg-gray-50 rounded border-l-2 border-indigo-400">
                        • {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2">Interview Talking Points</h4>
                  <ul className="space-y-1">
                    {marketplaceProfile.resume_language?.interview_talking_points?.map((point, idx) => (
                      <li key={idx} className="text-gray-700">• {point}</li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Section 8: Entrepreneur Fit */}
            <Card className="mb-8 border-2 border-amber-200">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Rocket className="h-6 w-6 text-amber-600" />
                  Entrepreneur & Side-Hustle Fit
                </CardTitle>
                <CardDescription>Business models suited to your wiring</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h4 className="font-semibold text-amber-800 mb-2">🚀 Best Business Models for You</h4>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {marketplaceProfile.entrepreneur_fit?.business_model_fit?.map((model, idx) => (
                      <Badge key={idx} variant="outline" className="border-amber-400 text-amber-800">{model}</Badge>
                    ))}
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-white rounded-lg border">
                    <h4 className="font-semibold text-gray-800 mb-2">Solo vs. Team</h4>
                    <p className="text-gray-700">{marketplaceProfile.entrepreneur_fit?.solo_vs_team}</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg border">
                    <h4 className="font-semibold text-gray-800 mb-2">Partner With</h4>
                    <p className="text-gray-700">{marketplaceProfile.entrepreneur_fit?.partner_with}</p>
                  </div>
                </div>
                
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <h4 className="font-semibold text-orange-800 mb-2">⚠️ Watch Out For</h4>
                  <p className="text-gray-700">{marketplaceProfile.entrepreneur_fit?.watch_out_for}</p>
                </div>
              </CardContent>
            </Card>

            {/* Section 9: Leadership Style */}
            <Card className="mb-8 border-2 border-violet-200">
              <CardHeader className="bg-gradient-to-r from-violet-50 to-purple-50">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Crown className="h-6 w-6 text-violet-600" />
                  Your Leadership Style
                </CardTitle>
                <CardDescription>How you lead and motivate others</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="p-4 bg-violet-100 rounded-lg text-center">
                  <div className="text-sm text-violet-600 uppercase tracking-wide">Leadership Type</div>
                  <div className="text-2xl font-bold text-violet-800">{marketplaceProfile.leadership_style?.leadership_type}</div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-white rounded-lg border">
                    <h4 className="font-semibold text-gray-800 mb-2">How You Lead</h4>
                    <p className="text-gray-700 text-sm">{marketplaceProfile.leadership_style?.how_you_lead}</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg border">
                    <h4 className="font-semibold text-gray-800 mb-2">How You Motivate</h4>
                    <p className="text-gray-700 text-sm">{marketplaceProfile.leadership_style?.how_you_motivate}</p>
                  </div>
                </div>
                
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-semibold text-purple-800 mb-2">Decision Making Style</h4>
                  <p className="text-gray-700">{marketplaceProfile.leadership_style?.decision_making}</p>
                </div>
                
                <div className="p-4 bg-indigo-50 rounded-lg">
                  <h4 className="font-semibold text-indigo-800 mb-2">🎯 Leadership Growth Areas</h4>
                  <ul className="space-y-1">
                    {marketplaceProfile.leadership_style?.leadership_growth_areas?.map((area, idx) => (
                      <li key={idx} className="text-gray-700">→ {area}</li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Section 10: Stress Signals */}
            <Card className="mb-8 border-2 border-red-200">
              <CardHeader className="bg-gradient-to-r from-red-50 to-rose-50">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Battery className="h-6 w-6 text-red-600" />
                  Stress Signals & Sustainability
                </CardTitle>
                <CardDescription>Burnout prevention and restoration</CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-2">⚡ Early Warning Signs You're Drained</h4>
                  <ul className="space-y-1">
                    {marketplaceProfile.stress_signals?.early_warning_signs?.map((sign, idx) => (
                      <li key={idx} className="text-gray-700">• {sign}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <h4 className="font-semibold text-orange-800 mb-2">What Drains You</h4>
                    <ul className="space-y-1">
                      {marketplaceProfile.stress_signals?.what_drains_you?.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-700">✗ {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-2">What Restores You</h4>
                    <ul className="space-y-1">
                      {marketplaceProfile.stress_signals?.what_restores_you?.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-700">✓ {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2">🛡️ Burnout Prevention</h4>
                  <p className="text-gray-700">{marketplaceProfile.stress_signals?.burnout_prevention}</p>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 justify-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700">
                <Download className="mr-2 h-5 w-5" />
                Download Report
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-56">
              <DropdownMenuItem onClick={handleDownloadMinistryPDF} className="cursor-pointer">
                <Heart className="mr-2 h-4 w-4 text-indigo-600" />
                <span>Ministry Report</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadMarketplacePDF} className="cursor-pointer">
                <Briefcase className="mr-2 h-4 w-4 text-emerald-600" />
                <span>Marketplace Report</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadCompletePDF} className="cursor-pointer">
                <FileText className="mr-2 h-4 w-4 text-purple-600" />
                <span>Complete Report (Both)</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            data-testid="retake-assessment-btn"
            onClick={() => navigate('/assessment')}
            variant="outline"
            size="lg"
          >
            Retake Assessment
          </Button>
          <Button
            data-testid="view-dashboard-btn"
            onClick={() => navigate('/dashboard')}
            variant="outline"
            size="lg"
          >
            Go to Dashboard
          </Button>
        </div>

        {/* Share Results */}
        <ShareResultsCard 
          userId={auth.user.id} 
          pairingCode={result.pairing_code} 
          userName={auth.user.name}
        />
      </div>
    </div>
  );
};

export default ResultsPage;
